import { useEffect, useState } from "react";

import { useCall } from "../state/CallProvider";
import type { CtiSettings } from "../state/settingsStore";

function Toggle({ label, hint, checked, onChange }: {
  label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <button type="button" role="switch" aria-checked={checked}
      className={"set-toggle" + (checked ? " on" : "")} onClick={() => onChange(!checked)}>
      <span className="set-toggle-text">
        <span className="set-toggle-label">{label}</span>
        {hint && <span className="set-toggle-hint">{hint}</span>}
      </span>
      <span className="set-switch" aria-hidden><span className="set-knob" /></span>
    </button>
  );
}

interface DeviceOption { id: string; label: string; }
const canPickSpeaker = typeof document !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;

export function Settings() {
  const { state, hostName, settings, setSetting } = useCall();
  const [mics, setMics] = useState<DeviceOption[]>([]);
  const [speakers, setSpeakers] = useState<DeviceOption[]>([]);

  // Enumerate audio devices. Labels are only exposed once mic permission is
  // granted, so ask for it (best-effort) then list; re-list if devices change.
  useEffect(() => {
    let done = false;
    const md = navigator.mediaDevices;
    if (!md?.enumerateDevices) return;
    const refresh = async () => {
      try {
        const devs = await md.enumerateDevices();
        if (done) return;
        const opt = (d: MediaDeviceInfo, i: number, k: string) => ({ id: d.deviceId, label: d.label || `${k} ${i + 1}` });
        setMics(devs.filter((d) => d.kind === "audioinput").map((d, i) => opt(d, i, "Microphone")));
        setSpeakers(devs.filter((d) => d.kind === "audiooutput").map((d, i) => opt(d, i, "Speaker")));
      } catch { /* ignore */ }
    };
    (async () => {
      try { (await md.getUserMedia({ audio: true })).getTracks().forEach((t) => t.stop()); } catch { /* denied → labels blank */ }
      await refresh();
    })();
    md.addEventListener?.("devicechange", refresh);
    return () => { done = true; md.removeEventListener?.("devicechange", refresh); };
  }, []);

  // Desktop notifications need the browser's permission — request it when the
  // agent turns the toggle on; keep it off if they deny.
  const onNotifications = async (v: boolean) => {
    if (v && "Notification" in window && Notification.permission === "default") {
      try { await Notification.requestPermission(); } catch { /* ignore */ }
    }
    const ok = v && "Notification" in window && Notification.permission === "granted";
    setSetting("notifications", ok);
  };

  const set = <K extends keyof CtiSettings>(k: K) => (v: CtiSettings[K]) => setSetting(k, v);

  return (
    <div className="set">
      <div className="set-group">
        <div className="set-group-title">Calls</div>
        <Toggle label="Auto-answer" hint="Answer incoming calls automatically"
          checked={settings.autoAnswer} onChange={set("autoAnswer")} />
        <Toggle label="Do not disturb" hint="Decline incoming calls without going offline"
          checked={settings.dnd} onChange={set("dnd")} />
        <Toggle label="Ringtone" hint="Play a sound while a call is ringing"
          checked={settings.ringtone} onChange={set("ringtone")} />
        <Toggle label="Desktop notifications" hint="Show a browser notification on incoming calls"
          checked={settings.notifications} onChange={onNotifications} />
      </div>

      <div className="set-group">
        <div className="set-group-title">Audio</div>
        <div className="set-row">
          <label>Microphone</label>
          <select value={settings.micDeviceId} onChange={(e) => setSetting("micDeviceId", e.target.value)}>
            <option value="">System default</option>
            {mics.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </div>
        <div className="set-row">
          <label>Speaker</label>
          <select value={settings.speakerDeviceId} disabled={!canPickSpeaker}
            onChange={(e) => setSetting("speakerDeviceId", e.target.value)}>
            <option value="">{canPickSpeaker ? "System default" : "Not supported in this browser"}</option>
            {canPickSpeaker && speakers.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </div>
      </div>

      <div className="set-group">
        <div className="set-group-title">Session</div>
        <div className="set-kv"><span>Signed in as</span><b>{state.auth.agent?.id ?? "—"}</b></div>
        <div className="set-kv"><span>CRM host</span><b>{hostName}</b></div>
        <div className="set-kv"><span>Channel</span><b>voice</b></div>
        <div className="set-kv"><span>Connector</span><b>Metafore CTI 0.1</b></div>
      </div>
    </div>
  );
}
